import React from 'react';

import { margin } from '@lumapps/classnames';
import { mdiReload } from '@lumapps/lumx/icons';
import {
    Alignment,
    Button,
    Emphasis,
    FlexBox,
    Message,
    Orientation,
    Placement,
    Size,
    Text,
    Typography,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { AI } from '../../keys';

interface AiAuthorizationSuccessCardProps {
    /** Method used to remake the AI query */
    refetch?: () => void;
    /** Translation of the success message. Defaults to the standard success message. */
    message?: string;
    /** Custom icon for refetch button. Defaults to mdiReload. Pass null to render no icon. */
    buttonIcon?: string | null;
    /** Custom message for refetch action */
    refetchButtonLabel?: string;
}

/**
 * Component displayed in an AI answer if an authErrored connector has been connected.
 * It displays a message and a reload button meant to re make the initial AI query.
 *
 * @param {AiAuthorizationSuccessCardProps} props - Optional `refetch` method and `message` key.
 * @returns {AiAuthorizationSuccessCard} - The rendered component.
 */
export const AiAuthorizationSuccessCard = ({
    refetch,
    message,
    buttonIcon = mdiReload,
    refetchButtonLabel,
}: AiAuthorizationSuccessCardProps) => {
    const { translateKey } = useTranslate();
    return (
        <Message
            kind="success"
            hasBackground
            style={{ alignItems: 'center' }} // This style ensures the icon is properly aligned
        >
            <FlexBox orientation={Orientation.horizontal} hAlign={Alignment.center} vAlign={Alignment.spaceBetween}>
                <Text as="p" typography={Typography.body1} className={margin(Placement.RIGHT, Size.regular)}>
                    {message || translateKey(AI.AUTHORIZATION_SUCCESS_MESSAGE)}
                </Text>
                {refetch && (
                    <Button leftIcon={buttonIcon ?? undefined} onClick={refetch} emphasis={Emphasis.medium}>
                        {refetchButtonLabel || translateKey(GLOBAL.RELOAD)}
                    </Button>
                )}
            </FlexBox>
        </Message>
    );
};
